import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface PortalBranding {
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  companyName: string;
}

interface PortalThemeContextType {
  branding: PortalBranding | null;
  isLoading: boolean;
}

const PortalThemeContext = createContext<PortalThemeContextType>({
  branding: null,
  isLoading: true,
});

export const usePortalTheme = () => useContext(PortalThemeContext);

export function PortalThemeProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<PortalBranding | null>(null);

  // Fetch contractor's company branding
  const { data: contractorData, isLoading } = useQuery({
    queryKey: ['contractor-portal-branding'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get contractor portal user record
      const { data: portalUser, error: portalError } = await supabase
        .from('contractor_portal_users')
        .select('company_id, tenant_id, companies(name)')
        .eq('auth_user_id', user.id)
        .single();

      if (portalError) throw portalError;

      // Get tenant branding from the tenants table
      const { data: tenantBranding, error: tenantError } = await supabase
        .from('tenants')
        .select('portal_logo_url, portal_primary_color, portal_secondary_color')
        .eq('id', portalUser.tenant_id)
        .single();

      if (tenantError) {
        console.error('Error fetching tenant branding:', tenantError);
      }

      // Return tenant-specific branding or defaults
      return {
        logoUrl: tenantBranding?.portal_logo_url || null,
        primaryColor: tenantBranding?.portal_primary_color || '#3b82f6',
        secondaryColor: tenantBranding?.portal_secondary_color || '#8b5cf6',
        companyName: (portalUser.companies as any)?.name || 'Company Portal',
      };
    },
    enabled: true,
  });

  useEffect(() => {
    if (contractorData) {
      setBranding(contractorData);
      
      // Apply CSS variables
      document.documentElement.style.setProperty('--portal-primary', contractorData.primaryColor);
      document.documentElement.style.setProperty('--portal-secondary', contractorData.secondaryColor);
    }
  }, [contractorData]);

  return (
    <PortalThemeContext.Provider value={{ branding, isLoading }}>
      {children}
    </PortalThemeContext.Provider>
  );
}
